/**
 * signup-bar custom element
 * Bell icon → modal drawer with email + phone form → Klaviyo subscription.
 *
 * Uses document-level event delegation for the trigger so it works regardless
 * of connectedCallback timing. The <dialog> is moved to <body> on first open
 * to escape any transform / overflow / stacking-context issues in the grid.
 */

// Delegate bell clicks at the document level — reliable even with deferred scripts
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-signup-open]');
  if (!trigger) return;

  const bar = trigger.closest('signup-bar');
  if (bar) bar.open();
});

class SignupBar extends HTMLElement {
  connectedCallback() {
    if (this._ready) return; // guard against re-connection
    this._ready = true;

    this._trigger = this.querySelector('[data-signup-open]');
    this._dialog  = this.querySelector('dialog');
    this._form    = this._dialog?.querySelector('[data-signup-form]');
    this._success = this._dialog?.querySelector('.signup-bar__success');
    this._error   = this._dialog?.querySelector('.signup-bar__error');

    if (!this._dialog) return;

    // Close button
    this._dialog.querySelectorAll('[data-signup-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    // Backdrop click closes
    this._dialog.addEventListener('click', (e) => {
      if (e.target === this._dialog) this.close();
    });

    // ESC key
    this._dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });

    this._form?.addEventListener('submit', (e) => this._onSubmit(e));
  }

  open() {
    if (!this._dialog) {
      // fallback: find dialog if connectedCallback hasn't run yet
      this._dialog = this.querySelector('dialog');
      if (!this._dialog) return;
    }

    // Teleport to body once — escapes grid transforms / overflow:hidden
    if (this._dialog.parentNode !== document.body) {
      document.body.appendChild(this._dialog);
    }

    this._dialog.removeAttribute('inert');

    try {
      this._dialog.showModal();
    } catch (_) {
      this._dialog.setAttribute('open', '');
    }

    // Focus the phone input after the slide-in animation settles
    const input = this._dialog.querySelector('input');
    if (input) setTimeout(() => input.focus(), 50);

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
  }

  close() {
    if (!this._dialog) return;
    try { this._dialog.close(); } catch (_) { this._dialog.removeAttribute('open'); }
    this._dialog.setAttribute('inert', '');
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    this._trigger?.focus();
  }

  _toE164(phone, countryCode = '1') {
    const digits = phone.replace(/\D/g, '');
    return digits.startsWith(countryCode) ? `+${digits}` : `+${countryCode}${digits}`;
  }

  async _onSubmit(e) {
    e.preventDefault();

    const email     = this._form.querySelector('[name="email"]')?.value.trim();
    const phoneRaw  = this._form.querySelector('[name="phone"]')?.value.trim();
    const phone     = phoneRaw ? this._toE164(phoneRaw) : null;
    const submitBtn = this._form.querySelector('.signup-bar__submit');

    if (!email) return;

    submitBtn.disabled = true;
    if (this._error) this._error.hidden = true;

    try {
      const companyId = (this.dataset.companyId || '').trim();
      const listId    = (this.dataset.listId || '').trim();

      if (!companyId || !listId) {
        console.error('Klaviyo: set Theme settings → Klaviyo Public API Key (Site ID) and Klaviyo List ID.');
        if (this._error) this._error.hidden = false;
        return;
      }

      const productContext = {
        waitlist_product_id:     this.dataset.productId || '',
        waitlist_product_handle: this.dataset.productHandle || '',
        waitlist_product_title:  this.dataset.productTitle || '',
        waitlist_product_url:    this.dataset.productUrl || '',
        waitlist_variant_id:     this.dataset.variantId || '',
        waitlist_variant_title:  this.dataset.variantTitle || '',
      };

      // Queue identify if Klaviyo onsite script is present
      var _learnq = window._learnq || [];
      const identifyProps = { $email: email, 'Waitlist Signup': true, ...productContext };
      if (phone) identifyProps.$phone_number = phone;
      _learnq.push(['identify', identifyProps]);

      const profileAttributes = {
        email: email,
        subscriptions: {
          email: { marketing: { consent: 'SUBSCRIBED' } },
        },
        properties: { ...productContext, source: 'Shopify theme signup bar' },
      };
      if (phone) profileAttributes.phone_number = phone;

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Shopify theme signup bar',
            profile: {
              data: { type: 'profile', attributes: profileAttributes },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      };

      const res = await fetch(
        'https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Accept: 'application/vnd.api+json',
            revision: '2025-10-15',
          },
          body: JSON.stringify(payload),
        }
      );

      const responseText = await res.text();
      if (res.ok) {
        this._form.hidden = true;
        if (this._success) this._success.hidden = false;
      } else {
        console.error('Klaviyo error', res.status, responseText);
        if (this._error) this._error.hidden = false;
      }
    } catch (err) {
      console.error('Network error:', err);
      if (this._error) this._error.hidden = false;
    } finally {
      submitBtn.disabled = false;
    }
  }
}

// Guard against duplicate registration.
if (!customElements.get('signup-bar')) {
  customElements.define('signup-bar', SignupBar);
}
